import React, { useEffect, useState } from 'react';
import { useAdminStore } from '../store/useAdminStore';

function AdminSecurity() {
  const { securityThreats: threats, setSecurityThreats: setThreats } = useAdminStore();
  // Stable load metric — computed once on mount, not on every re-render
  const [systemLoad] = useState(() => Math.floor(Math.random() * (30 - 10 + 1) + 10));

  useEffect(() => {
    fetch('/api/admin/security/threats', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => res.json())
      .then(data => setThreats(Array.isArray(data) ? data : []))
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAsRead = async (id) => {
    try {
      const res = await fetch(`/api/admin/security/threats/${id}/read`, {
        method: 'PATCH',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setThreats(threats.map(t => t.id === id ? { ...t, is_read: true } : t));
      }
    } catch (error) {
      console.error('Failed to mark as read', error);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <span>🛡️</span> Active Threat Board
        </h1>
        <p className="text-gray-400 text-sm mt-1">Live monitoring of intercepted attacks, Honeypot triggers, and rate-limit violations.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-6 mb-8">
        <div className="flex-1 bg-[#132742]/80 backdrop-blur-xl p-6 rounded-2xl border border-red-500/20 shadow-lg relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <h3 className="text-red-200/80 text-xs font-bold uppercase tracking-widest">Active Threats</h3>
          <div className="mt-3 text-4xl font-black text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]">{threats.length}</div>
          <div className="mt-4 text-xs font-semibold text-red-400 flex items-center gap-1.5 bg-red-500/10 w-max px-2.5 py-1 rounded-md">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            Blocked by WAF
          </div>
        </div>

        <div className="flex-1 bg-[#132742]/80 backdrop-blur-xl p-6 rounded-2xl border border-white/5 shadow-lg relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <h3 className="text-cyan-200/60 text-xs font-bold uppercase tracking-widest">System Load</h3>
          <div className="mt-3 text-4xl font-black text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.4)]">{systemLoad}%</div>
          <div className="mt-4 text-xs font-semibold text-cyan-400 flex items-center gap-1.5 bg-cyan-500/10 w-max px-2.5 py-1 rounded-md">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            Optimal Connection
          </div>
        </div>
      </div>

      <div className="bg-gray-900 border border-red-900/50 rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-red-500 mb-4 flex items-center gap-2">
          <span>🚨</span> System Audit Trail & Threat Log
        </h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-400">
            <thead className="text-xs uppercase bg-gray-950 text-gray-500 border-b border-gray-800 whitespace-nowrap">
              <tr>
                <th className="px-6 py-4 font-bold">Action / IP</th>
                <th className="px-6 py-4 font-bold">Device Fingerprint</th>
                <th className="px-6 py-4 font-bold">Details</th>
                <th className="px-6 py-4 font-bold">Timestamp</th>
                <th className="px-6 py-4 font-bold text-right">Status</th>
              </tr>
            </thead>
            <tbody className="whitespace-nowrap">
              {threats.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500 italic">No logs found.</td>
                </tr>
              ) : (
                threats.slice(0, 100).map(threat => (
                  <tr key={threat.id} className={`border-b border-gray-800/50 hover:bg-gray-950/50 transition-colors ${threat.is_read ? 'opacity-40' : ''}`}>
                    <td className="px-6 py-4 font-mono">
                      <span className={threat.action === 'BANNED' ? 'text-red-500 font-bold' : 'text-blue-400 font-bold'}>{threat.action}</span>
                      <br/>
                      <span className="text-xs text-gray-500">{threat.ip_address || 'UNKNOWN'}</span>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs">{threat.device_id || 'N/A'}</td>
                    <td className="px-6 py-4 text-white font-medium">{threat.details}</td>
                    <td className="px-6 py-4 text-gray-500">{new Date(threat.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      {threat.is_read ? (
                        <span className="text-green-500/80 text-xs font-bold uppercase tracking-wider flex justify-end items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                          Read
                        </span>
                      ) : (
                        <button 
                          onClick={() => markAsRead(threat.id)}
                          className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-xs font-semibold px-3 py-1.5 rounded transition-colors"
                        >
                          Mark as Read
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminSecurity;
